import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitInquiry } from '../../firebase/firestore';
import Button from './Button';
import './ServiceContactForm.css';

const ServiceContactForm = ({ serviceSlug = '' }) => {
    const { t, i18n } = useTranslation();

    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
    const [errors, setErrors] = useState({});
    const [formStatus, setFormStatus] = useState({ submitting: false, success: false, error: null });

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = t('contact.form.required');
        if (!formData.email.trim()) {
            newErrors.email = t('contact.form.required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('contact.form.invalidEmail');
        }
        if (!formData.phone.trim()) newErrors.phone = t('contact.form.required');
        if (!formData.message.trim()) newErrors.message = t('contact.form.required');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setFormStatus({ submitting: true, success: false, error: null });
        try {
            await submitInquiry({ ...formData, serviceSlug, language: i18n.language });
            setFormStatus({ submitting: false, success: true, error: null });
            setFormData({ name: '', email: '', phone: '', message: '' });
            setTimeout(() => setFormStatus(prev => ({ ...prev, success: false })), 6000);
        } catch (err) {
            setFormStatus({ submitting: false, success: false, error: t('contact.form.error') });
        }
    };

    return (
        <div className="service-contact-form">
            <h2 className="scf-title">
                {i18n.language === 'ar' ? 'اطلب استشارة مجانية' : 'Request a Free Consultation'}
            </h2>
            <p className="scf-subtitle">
                {i18n.language === 'ar'
                    ? 'تواصل معنا وسيرد عليك فريقنا خلال 24 ساعة'
                    : 'Reach out and our team will respond within 24 hours'}
            </p>

            {formStatus.success && (
                <div className="scf-alert scf-alert--success">
                    ✓ {t('contact.form.success')}
                </div>
            )}
            {formStatus.error && (
                <div className="scf-alert scf-alert--error">
                    ✗ {formStatus.error}
                </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="scf-form">
                <div className="scf-row">
                    <div className="scf-field">
                        <label htmlFor={`scf-name-${serviceSlug}`}>{t('contact.form.name')} *</label>
                        <input
                            id={`scf-name-${serviceSlug}`}
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={errors.name ? 'scf-input error' : 'scf-input'}
                            placeholder={t('contact.form.name')}
                        />
                        {errors.name && <span className="scf-error">{errors.name}</span>}
                    </div>
                    <div className="scf-field">
                        <label htmlFor={`scf-phone-${serviceSlug}`}>{t('contact.form.phone')} *</label>
                        <input
                            id={`scf-phone-${serviceSlug}`}
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={errors.phone ? 'scf-input error' : 'scf-input'}
                            placeholder="+20 ..."
                        />
                        {errors.phone && <span className="scf-error">{errors.phone}</span>}
                    </div>
                </div>

                <div className="scf-field">
                    <label htmlFor={`scf-email-${serviceSlug}`}>{t('contact.form.email')} *</label>
                    <input
                        id={`scf-email-${serviceSlug}`}
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? 'scf-input error' : 'scf-input'}
                        placeholder="email@example.com"
                    />
                    {errors.email && <span className="scf-error">{errors.email}</span>}
                </div>

                <div className="scf-field">
                    <label htmlFor={`scf-message-${serviceSlug}`}>{t('contact.form.message')} *</label>
                    <textarea
                        id={`scf-message-${serviceSlug}`}
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className={errors.message ? 'scf-input error' : 'scf-input'}
                        placeholder={i18n.language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                    />
                    {errors.message && <span className="scf-error">{errors.message}</span>}
                </div>

                <Button
                    type="submit"
                    variant="primary"
                    disabled={formStatus.submitting}
                    className="scf-submit"
                >
                    {formStatus.submitting ? t('contact.form.sending') : t('contact.form.submit')}
                </Button>
            </form>
        </div>
    );
};

export default ServiceContactForm;
